import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "../App";
import { Button } from "../components/ui/button";
import { Bed, UtensilsCrossed, Users, Tag, Clock, Wifi, Tv, Wind, ArrowRight, MapPin, HelpCircle } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const ICON_MAP = {
  bed: Bed, utensils: UtensilsCrossed, users: Users, tag: Tag, clock: Clock,
};

const DEFAULT_FEATURES = [
  { title: "Comfortable Rooms", icon: "bed" },
  { title: "Pure Veg Restaurant", icon: "utensils" },
  { title: "Family Friendly Stay", icon: "users" },
  { title: "Affordable Pricing", icon: "tag" },
  { title: "24/7 Reception", icon: "clock" },
];

const AMENITY_ICONS = { "Free WiFi": Wifi, "WiFi": Wifi, "Smart TV": Tv, "TV": Tv, "AC": Wind, "Work Desk": Tag, "Almira": Tag, "Double Bed": Bed };

const FAQ_DATA = [
  { q: "What is the best hotel in Dungarpur?", a: "Ambe Palace is widely regarded as the best hotel in Dungarpur, Rajasthan. Located on Sagwara Road, Tijwaar, it offers comfortable rooms starting at just \u20B9849/night, a pure vegetarian restaurant, free WiFi, AC rooms, and 24/7 reception." },
  { q: "Is there a good hotel near Dungarpur?", a: "Yes! Ambe Palace is a top-rated hotel located right in the heart of Dungarpur. It is easily accessible from all major roads and offers premium hospitality, making it the best choice for travelers visiting Dungarpur." },
  { q: "What are the room prices at Ambe Palace?", a: "Room prices at Ambe Palace range from \u20B9849/night (Deluxe Room) to \u20B91349/night (Premium Suite). All rooms include WiFi and TV. Extra person charge is \u20B9200/night, and 5% GST applies." },
  { q: "Does Ambe Palace have a restaurant?", a: "Yes, Ambe Palace features an in-house pure vegetarian restaurant open from 10 AM to 12 AM, serving a wide variety of Indian dishes, starters, main course, breads, rice items, and desserts." },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { content, rooms } = useContent();

  usePageSEO({
    title: "Ambe Palace | Best Hotel in Dungarpur, Rajasthan",
    description: "Looking for the best hotel in Dungarpur? Ambe Palace offers comfortable rooms, great amenities, and top-rated hospitality in the heart of Dungarpur, Rajasthan. Book your stay today."
  });

  const hero = content.hero || {
    headline: "Welcome to Ambe Palace \u2013 Best Hotel in Dungarpur",
    subtext: "Comfortable rooms, delicious pure veg dining, and a peaceful stay experience in the heart of Dungarpur.",
    image: "https://customer-assets.emergentagent.com/job_8fdea702-ef33-49ea-a556-0fc99f37063c/artifacts/ekysft7c_ChatGPT%20Image%20Mar%209%2C%202026%2C%2010_46_36%20AM.png"
  };

  const about = content.about || {
    title: "About Ambe Palace",
    text: "Ambe Palace offers comfortable stays with clean rooms, quality service, and a pure vegetarian restaurant. Located on Sagwara Road, Tijwaar, the hotel provides a perfect place for travelers and families visiting Dungarpur."
  };

  const features = Array.isArray(content.features) ? content.features : DEFAULT_FEATURES;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[500px] overflow-hidden" data-testid="hero-section">
        <img src={hero.image} alt="Ambe Palace - Best hotel in Dungarpur Rajasthan exterior view" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex items-end pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-green-600/90 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 mb-6">
                <span className="w-2 h-2 bg-white rounded-full" />
                Pure Veg Restaurant
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
                Best Hotel in Dungarpur {'\u2014'} Ambe Palace
              </h1>
              <p className="text-base md:text-lg text-stone-200 leading-relaxed mb-8 max-w-xl">
                {hero.subtext}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate("/booking")}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none px-8 py-3 h-12 shadow-lg hover:shadow-xl transition-shadow duration-200"
                  data-testid="hero-book-room-btn"
                >
                  Book Room
                </Button>
                <Button
                  onClick={() => navigate("/rooms")}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 text-xs font-bold uppercase tracking-[0.15em] rounded-none px-8 py-3 h-12"
                  data-testid="hero-view-rooms-btn"
                >
                  View Rooms
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">About Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Top-Rated Hotel in Dungarpur, Rajasthan
            </h2>
            <div className="w-16 h-[3px] bg-amber-600 mx-auto mb-8" />
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              {about.text}
            </p>
            <p className="text-base text-slate-500 leading-relaxed mt-4">
              Whether you are looking for a comfortable stay in Dungarpur or searching for the best hotel near Dungarpur, Ambe Palace is your ideal choice. We offer affordable pricing, a family-friendly atmosphere, and a memorable hospitality experience in the heart of Rajasthan.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-stone-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Ambe Palace is the Best Hotel in Dungarpur
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {features.map((feat, i) => {
              const IconComp = ICON_MAP[feat.icon] || Bed;
              return (
                <div
                  key={i}
                  className="bg-white p-6 text-center border border-stone-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 group"
                  data-testid={`feature-${i}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors duration-300">
                    <IconComp className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{feat.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Room Preview Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="room-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">Accommodation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Book Hotel Rooms in Dungarpur
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(rooms.length > 0 ? rooms : []).map((room) => (
              <div
                key={room.id}
                className="group border border-stone-100 bg-white hover:shadow-xl transition-all duration-300 overflow-hidden"
                data-testid={`room-card-${room.id}`}
              >
                <div className="img-zoom aspect-[4/3] overflow-hidden">
                  <img src={room.image} alt={`${room.name} at Ambe Palace hotel in Dungarpur Rajasthan`} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{room.name}</h3>
                  <p className="text-amber-600 font-bold text-lg mb-3">{'\u20B9'}{room.price} <span className="text-xs text-slate-400 font-normal">/ night</span></p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {room.amenities.map((a, i) => {
                      const AIcon = AMENITY_ICONS[a] || Tag;
                      return (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-stone-50 px-2 py-1 uppercase tracking-wider">
                          <AIcon className="w-3 h-3" />{a}
                        </span>
                      );
                    })}
                  </div>
                  <Button
                    onClick={() => navigate(`/booking?room=${room.id}`)}
                    disabled={room.available === false}
                    className={`w-full text-xs font-bold uppercase tracking-[0.15em] rounded-none h-10 transition-colors duration-200 ${
                      room.available === false
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                    data-testid={`book-${room.id}-btn`}
                  >
                    {room.available === false ? 'Room Full' : <><span>Book Now</span> <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Near Me / Location Section */}
      <section className="py-16 md:py-20 bg-slate-900" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <MapPin className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Find Us in Dungarpur, Rajasthan
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-3">
              Located in the heart of Dungarpur, Rajasthan — Ambe Palace is easily findable when you search "hotel near me" in or around Dungarpur. We are situated on Sagwara Road, Tijwaar, with convenient access from all major routes.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Dungarpur, Sagwara Road, Tijwaar, Rajasthan 314001
            </p>
            <a href="https://share.google/mvDbvq07DpUPnSWH3" target="_blank" rel="noopener noreferrer">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none px-8 h-11" data-testid="home-get-direction-btn">
                Get Directions on Google Maps
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-stone-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {FAQ_DATA.map((faq, i) => (
              <details key={i} className="bg-white border border-stone-100 group" data-testid={`faq-${i}`}>
                <summary className="flex items-center gap-3 p-5 cursor-pointer list-none">
                  <HelpCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <h3 className="text-sm font-bold text-slate-800">{faq.q}</h3>
                </summary>
                <div className="px-5 pb-5 pl-13">
                  <p className="text-sm text-slate-600 leading-relaxed ml-8">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
